/**
 * Routing and the authentication gate.
 *
 * The gate is structural, not decorative: every application route is a child of
 * `AppShell`, which only renders once a session exists. There is no public
 * route and no anonymous view of operational data (spec section 4). The Worker
 * enforces the same thing independently — a signed-in user without an active
 * `app_users` row gets 403s and an app full of permission errors, which is the
 * intended outcome rather than a bug.
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { useT } from './i18n';
import { AppShell } from './components/layout/AppShell';
import { LoginPage } from './features/auth/LoginPage';
import { LoadingBlock } from './components/ui/states';
import { OverviewPage } from './features/dashboard/OverviewPage';
import {
  AllianceActivityPage,
  AllianceGrowthPage,
  AllianceMembersPage,
  AllianceRolesPage,
} from './features/alliance/AlliancePages';
import { PlayerDetailPage } from './features/players/PlayerDetailPage';
import { MyBearPage, PlayerBearPage } from './features/bear/PersonalBearPage';
import { BearPlannerPage } from './features/bear/BearPlannerPage';
import { BearHistoryPage } from './features/bear/BearHistoryPage';
import {
  BearExperimentsPage,
  CastlePage,
  ChampionshipPage,
  FortressPage,
  MobilizationPage,
  SwordlandPage,
  TriAlliancePage,
  VikingPage,
} from './features/events/EventPages';
import {
  StateAlliancesPage,
  StateKvkPage,
  StateLeaderboardsPage,
  StatePlayersPage,
  StateTransfersPage,
} from './features/state-intel/StateIntelPages';
import {
  CatalogPage,
  FreshnessPage,
  RulesPage,
  SettingsPage,
  SyncStatusPage,
} from './features/data/DataPages';
import { RallyLabPage } from './features/rally/RallyLabPage';
import { NotFoundPage } from './features/NotFoundPage';

export function App() {
  const { state } = useAuth();
  const t = useT();

  if (state.status === 'loading') {
    return (
      <div style={{ padding: 40, maxWidth: 480, margin: '0 auto' }}>
        <LoadingBlock label={t.shell.restoringSession} rows={2} />
      </div>
    );
  }

  if (state.status === 'signed-out') {
    return <LoginPage />;
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<OverviewPage />} />

          <Route path="alliance">
            <Route index element={<Navigate to="/alliance/members" replace />} />
            <Route path="members" element={<AllianceMembersPage />} />
            <Route path="growth" element={<AllianceGrowthPage />} />
            <Route path="activity" element={<AllianceActivityPage />} />
            <Route path="roles" element={<AllianceRolesPage />} />
          </Route>

          <Route path="players/:playerId" element={<PlayerDetailPage />} />

          <Route path="bear">
            <Route index element={<Navigate to="/bear/me" replace />} />
            <Route path="me" element={<MyBearPage />} />
            <Route path="player/:playerId" element={<PlayerBearPage />} />
            <Route path="planner" element={<BearPlannerPage />} />
            <Route path="history" element={<BearHistoryPage />} />
            <Route path="experiments" element={<BearExperimentsPage />} />
          </Route>

          <Route path="rally" element={<RallyLabPage />} />

          <Route path="events">
            <Route index element={<Navigate to="/events/championship" replace />} />
            <Route path="championship" element={<ChampionshipPage />} />
            <Route path="swordland" element={<SwordlandPage />} />
            <Route path="fortress" element={<FortressPage />} />
            <Route path="castle" element={<CastlePage />} />
            <Route path="viking" element={<VikingPage />} />
            <Route path="tri-alliance" element={<TriAlliancePage />} />
            <Route path="mobilization" element={<MobilizationPage />} />
          </Route>

          <Route path="state">
            <Route index element={<Navigate to="/state/alliances" replace />} />
            <Route path="alliances" element={<StateAlliancesPage />} />
            <Route path="players" element={<StatePlayersPage />} />
            <Route path="transfers" element={<StateTransfersPage />} />
            <Route path="leaderboards" element={<StateLeaderboardsPage />} />
            <Route path="kvk" element={<StateKvkPage />} />
          </Route>

          <Route path="data">
            <Route index element={<Navigate to="/data/sync" replace />} />
            <Route path="freshness" element={<FreshnessPage />} />
            <Route path="rules" element={<RulesPage />} />
            <Route path="catalog" element={<CatalogPage />} />
            <Route path="sync" element={<SyncStatusPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
